import React, { useCallback, useState, useRef, useEffect } from 'react';
import { OverlayView, Polygon, DrawingManager } from '@react-google-maps/api';
import { useSelector } from 'react-redux';
import { setEditPathOfPolygon } from 'store/requests/zone';
import { getZoneOptions } from 'helpers/utils';
import { SHAPE_TYPE } from 'helpers/constants';
import { setDrawnPolygonPaths } from '../../../store/requests/zone';

const ViewSelectedZoneForEdit = (props) => {
  const { polygonRef, onLoad, onUnmount } = props;

  const drawingManagerRef = useRef(null);

  const generateGeoJSONCircle = (center, radius, numSides) => {
    let points = [],
      degreeStep = 360 / numSides;

    for (let i = 0; i < numSides; i++) {
      let gpos = new window.google.maps.geometry.spherical.computeOffset(
        center,
        radius,
        degreeStep * i,
      );
      points.push([gpos.lng(), gpos.lat()]);
    }
    points.push(points[0]);

    return {
      type: 'Polygon',
      coordinates: [points],
    };
  };

  const zoneState = useSelector((state) => state.zone.geoRadarZone) ?? null;
  const [contextMenuLatLng, setContextMenuLatLng] = useState(null);
  const [showContextMenu, setShowContextMenu] = useState(false);
  const [contextMenuPosition, setContextMenuPosition] = useState({
    top: 0,
    left: 0,
  });

  const contextMenuRef = useRef(null);

  const options = getZoneOptions({
    fillOpacity: zoneState?.zoneOpacity,
    fillColor: zoneState?.zoneColor?.color,
  });

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        contextMenuRef.current &&
        !contextMenuRef.current.contains(event.target)
      ) {
        setShowContextMenu(false);
      }
    };

    window.addEventListener('click', handleClickOutside);
    return () => {
      window.removeEventListener('click', handleClickOutside);
    };
  }, []);

  const onEdit = useCallback(() => {
    if (polygonRef.current) {
      const nextPath = polygonRef.current
        .getPath()
        .getArray()
        .map((latLng) => {
          return { lat: latLng.lat(), lng: latLng.lng() };
        });
      setEditPathOfPolygon(nextPath);
    }
  }, [setEditPathOfPolygon]);

  const onRightClick = useCallback(
    (e) => {
      if (polygonRef.current) {
        const path = polygonRef.current.getPath();
        const clickedPointIndex = path.getArray().indexOf(e.latLng);
        if (clickedPointIndex !== -1) {
          setContextMenuLatLng(e.latLng);
          setContextMenuPosition({ top: e?.pixel?.y, left: e?.pixel?.x });
          setShowContextMenu(true);
        }
      }
    },
    [polygonRef],
  );

  const onDeleteClick = useCallback(() => {
    if (polygonRef.current && contextMenuLatLng) {
      const path = polygonRef.current.getPath();

      let clickedPointIndex = -1;
      path.forEach((latLng, index) => {
        if (
          latLng.lat() === contextMenuLatLng.lat() &&
          latLng.lng() === contextMenuLatLng.lng()
        ) {
          clickedPointIndex = index;
        }
      });

      if (clickedPointIndex !== -1) {
        if (clickedPointIndex === path.length - 1) {
          const firstPoint = path.getAt(0);
          const lastPoint = path.getAt(path.length - 1);

          if (
            firstPoint.lat() === lastPoint.lat() &&
            firstPoint.lng() === lastPoint.lng()
          ) {
            path.removeAt(clickedPointIndex);
            path.removeAt(0);
          }
        } else {
          path.removeAt(clickedPointIndex);
        }
        onEdit();
      }
    }
    setContextMenuLatLng(null);
    setShowContextMenu(false);
  }, [polygonRef, contextMenuLatLng, onEdit]);

  const onShapesComplete = (shape, shapeType) => {
    setDrawnPolygonPaths(null);
    let latlngCoordinates = [];

    if (shapeType === SHAPE_TYPE.CIRCLE) {
      const radius = shape.getRadius();
      const center = shape.getCenter();

      const polygon = generateGeoJSONCircle(center, radius, 20);
      polygon.coordinates.forEach((latLon) => {
        latLon.forEach((ll) => {
          latlngCoordinates.push({
            lat: ll[1],
            lng: ll[0],
          });
        });
      });

      const newPolygon = new window.google.maps.Polygon({
        paths: latlngCoordinates,
        strokeColor: '#FF0000',
        strokeOpacity: 0.8,
        strokeWeight: 2,
        fillColor: zoneState?.zoneColor.color,
        fillOpacity: zoneState?.zoneOpacity,
      });
      setPolygonPaths(newPolygon);
      shape.setMap(null);
    } else if (shapeType === SHAPE_TYPE.RECTANGLE) {
      const rectangleBounds = shape.getBounds();
      const polygonDots = [
        {
          lat: rectangleBounds.getNorthEast().lat(),
          lng: rectangleBounds.getNorthEast().lng(),
        },
        {
          lat: rectangleBounds.getNorthEast().lat(),
          lng: rectangleBounds.getSouthWest().lng(),
        },
        {
          lat: rectangleBounds.getSouthWest().lat(),
          lng: rectangleBounds.getSouthWest().lng(),
        },
        {
          lat: rectangleBounds.getSouthWest().lat(),
          lng: rectangleBounds.getNorthEast().lng(),
        },
      ];

      const polygon = new window.google.maps.Polygon({
        paths: polygonDots,
        strokeColor: '#FF0000',
        strokeOpacity: 0.8,
        strokeWeight: 2,
        fillColor: zoneState?.zoneColor.color,
        fillOpacity: zoneState?.zoneOpacity,
      });

      setPolygonPaths(polygon);
      shape.setMap(null);
    } else {
      setPolygonPaths(shape);
    }
  };

  const setPolygonPaths = (polygon) => {
    let polygonDots = [];
    polygon
      .getPath()
      .getArray()
      .map((latLon) => {
        polygonDots.push({
          lat: latLon.lat(),
          lng: latLon.lng(),
        });
      });
    setDrawnPolygonPaths(polygonDots);
    setEditPathOfPolygon(polygonDots);
    polygon.setMap(null);
  };

  return (
    <React.Fragment>
      {zoneState?.selectedZoneItem?.zonePolygonJson && (
        <Polygon
          options={getZoneOptions({
            fillColor: zoneState?.zoneColor?.color,
            fillOpacity: zoneState?.zoneOpacity,
            suppressUndo: true,
          })}
          path={zoneState?.selectedZoneItem?.zonePolygonJson}
          onMouseUp={onEdit}
          onDragEnd={onEdit}
          onRightClick={onRightClick}
          onLoad={onLoad}
          onUnmount={onUnmount}
        />
      )}
      {!zoneState?.selectedZoneItem?.zonePolygonJson && (
        <DrawingManager
          ref={drawingManagerRef}
          onPolygonComplete={(polygon) =>
            onShapesComplete(polygon, SHAPE_TYPE.POLYGON)
          }
          onCircleComplete={(circle) =>
            onShapesComplete(circle, SHAPE_TYPE.CIRCLE)
          }
          onRectangleComplete={(rectangle) =>
            onShapesComplete(rectangle, SHAPE_TYPE.RECTANGLE)
          }
          drawingMode={'polygon'}
          options={{
            drawingControl: true,
            drawingControlOptions: {
              position: window.google.maps.ControlPosition.TOP_RIGHT,
              drawingModes: [
                window.google.maps.drawing.OverlayType.CIRCLE,
                window.google.maps.drawing.OverlayType.POLYGON,
                window.google.maps.drawing.OverlayType.RECTANGLE,
              ],
            },
            polygonOptions: options,
            circleOptions: options,
            rectangleOptions: options,
          }}
        />
      )}
      {showContextMenu && contextMenuLatLng && (
        <OverlayView
          position={contextMenuLatLng}
          mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
        >
          <div
            ref={contextMenuRef}
            style={{
              position: 'absolute',
              top: `${contextMenuPosition.top}px`,
              left: `${contextMenuPosition.left}px`,
              background: 'white',
              border: '1px solid black',
              padding: '5px',
              cursor: 'pointer',
            }}
          >
            <div onClick={onDeleteClick}>Delete</div>
          </div>
        </OverlayView>
      )}
    </React.Fragment>
  );
};

export default ViewSelectedZoneForEdit;
